import React, { ReactNode, useState } from 'react';

export interface TabProps {
  label: string;
  children: ReactNode;
}

export const Tab: React.FC<TabProps> = ({ children }) => {
  return <div>{children}</div>;
};

// Support both children-based and array-based interfaces
export interface TabsProps {
  children?: ReactNode;
  defaultTab?: number;
  // Array-based interface (used by CommunityInsights)
  tabs?: Array<{
    id: string;
    label: string;
    content: ReactNode;
  }>;
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
}

export const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  ({ children, defaultTab = 0, tabs, activeTab: controlledActiveTab, onTabChange }, ref) => {
    const [internalActiveTab, setInternalActiveTab] = useState(defaultTab);
    
    // Support array-based interface
    if (tabs && tabs.length > 0) {
      const activeIndex = tabs.findIndex(tab => tab.id === controlledActiveTab);
      const finalIndex = activeIndex >= 0 ? activeIndex : 0;
      
      return (
        <div ref={ref} className="tabs-container">
          <div className="tabs-header">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${controlledActiveTab === tab.id ? 'active' : ''}`}
                onClick={() => onTabChange && onTabChange(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="tab-content">
            {tabs[finalIndex]?.content}
          </div>
        </div>
      );
    }
    
    // Original children-based interface
    const activeTab = internalActiveTab;
    const setActiveTab = setInternalActiveTab;
    const tabsArray = React.Children.toArray(children);

    return (
      <div ref={ref} className="tabs-container">
        <div className="tabs-header">
          {React.Children.map(children, (child, index) => {
            if (React.isValidElement(child)) {
              return (
                <button
                  className={`tab-button ${activeTab === index ? 'active' : ''}`}
                  onClick={() => setActiveTab(index)}
                >
                  {child.props.label}
                </button>
              );
            }
            return null;
          })}
        </div>
        <div className="tab-content">
          {tabsArray[activeTab]}
        </div>
      </div>
    );
  }
);

Tabs.displayName = 'Tabs';

export default Tabs;